import React, { Fragment, PureComponent } from "react";
import { Box, Divider, Grid, FormControl, FormControlLabel, Checkbox, Typography, Radio, RadioGroup } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import CustomAcordion from "../../../../layout/CustomAcordion";
import LabeledTextField from "../../shared/ui/HaklaiLabeledTextField";
import HelpDialog from "../../../../layout/HelpDialog";
import helpTexts from "../../../../../assets/texts/helpTexts";
import {
  getHaklaiPrisaPersonTitle,
  getHaklaiPrisaYearsBackLabel,
  HAKLAI_PRISA_ERRORS,
  HAKLAI_PRISA_LABELS,
  HAKLAI_PRISA_MESSAGES
} from "../haklaiPrisaTexts";
import {
  HAKLAI_PRISA_CREDIT_POINT_GENDER,
  HAKLAI_PRISA_CREDIT_POINT_RESIDENCY
} from "../haklaiPrisaCreditPoints";
import { MAX_MONEY_AMOUNT } from "../../shared/haklaiConstants";
import { AMOUNT_INPUT_WIDTH } from "../../expenses/haklaiExpensesStyles";

// One text line. The slot stays this tall while the field is valid, and a
// message replaces the blank line instead of growing the row.
const PRISA_ERROR_LINE_HEIGHT = 16;
// Same 128px date pill as the acquisition dates, and the same 100px money
// pill as סכום ההוצאה. Selects keep only the option text plus the arrow pad.
const PRISA_DATE_FIELD_WIDTH = 128;
const PRISA_NAME_FIELD_WIDTH = 140;
const PRISA_INCOME_FIELD_WIDTH = AMOUNT_INPUT_WIDTH;
const PRISA_ZIKUI_FIELD_WIDTH = 52;
// Select chrome is the left caret pad (32) plus the Hebrew text pad (8).
// Each width is that chrome plus the longest option at 13.6px Rubik, plus 2px
// so the label does not ellipsize. Longest labels: "שנה אחת אחורה" (91),
// "אישה" (30), "תושב ישראל" (69).
const PRISA_SELECT_CHROME = 40;
const PRISA_SELECT_TEXT_SAFETY = 2;
const PRISA_YEARS_FIELD_WIDTH = 91 + PRISA_SELECT_CHROME + PRISA_SELECT_TEXT_SAFETY;
const PRISA_INCOME_MAX_VALUE = MAX_MONEY_AMOUNT;
const PRISA_ZIKUI_MAX_VALUE = 100;
// A client count is one digit, narrower than MUI's 16px select content minimum.
const CLIENTS_COUNT_FIELD_WIDTH = 16 + PRISA_SELECT_CHROME;
const CREDIT_POINT_MAXIMAL_STYLE = {
  height: 40,
  margin: 0
};
const CREDIT_POINTS_COMPACT_ERRORS = Object.freeze({
  [HAKLAI_PRISA_ERRORS.creditPointsRequired]: "חובה",
  [HAKLAI_PRISA_ERRORS.creditPointsInvalid]: "לא תקין"
});
const CREDIT_POINT_GENDER_FIELD_WIDTH = 30 + PRISA_SELECT_CHROME + PRISA_SELECT_TEXT_SAFETY;
const CREDIT_POINT_RESIDENCY_FIELD_WIDTH = 69 + PRISA_SELECT_CHROME + PRISA_SELECT_TEXT_SAFETY;
const CREDIT_POINT_GENDER_OPTIONS = [
  { label: HAKLAI_PRISA_LABELS.selectPlaceholder, value: "" },
  { label: HAKLAI_PRISA_LABELS.male, value: HAKLAI_PRISA_CREDIT_POINT_GENDER.MALE },
  { label: HAKLAI_PRISA_LABELS.female, value: HAKLAI_PRISA_CREDIT_POINT_GENDER.FEMALE }
];
const CREDIT_POINT_RESIDENCY_OPTIONS = [
  { label: HAKLAI_PRISA_LABELS.selectPlaceholder, value: "" },
  { label: HAKLAI_PRISA_LABELS.israeliResident, value: HAKLAI_PRISA_CREDIT_POINT_RESIDENCY.ISRAELI },
  { label: HAKLAI_PRISA_LABELS.foreignResident, value: HAKLAI_PRISA_CREDIT_POINT_RESIDENCY.FOREIGN }
];

const getPrisaIncomeDisplayValue = (value) => (
  value === null || value === undefined || value === "" || Number.isNaN(value) ? 0 : value
);

class HaklaiPrisaComponent extends PureComponent {
  render() {
    const fieldErrors = this.props.fieldErrors || {};
    const useHaklaiValidationLayout = this.props.useHaklaiValidation === true;
    // Maximal Prisa displays the sale year as zero but keeps its draft values in state,
    // allowing the user's inputs to return unchanged when maximal mode is turned off.
    const isDisabledMaximalYear = (person, yearIndex) => yearIndex === 0 && person.isMaximal;
    // Fields that can fail keep the message line while they are still valid.
    // The text appears in that line, so the rows below do not jump.
    const getShownErrorProps = (message, options = {}) => {
      if (!message && options.reserve !== true) return {};
      return {
        helperText: message || "\u00a0",
        helperTextMinHeight: PRISA_ERROR_LINE_HEIGHT,
        centerHelperText: true
      };
    };
    const getFieldValidationProps = (fieldName, options) => (
      useHaklaiValidationLayout
        ? {
            error: Boolean(fieldErrors[fieldName]),
            ...getShownErrorProps(fieldErrors[fieldName], options),
            onBlur: () => this.handleFieldBlur(fieldName)
          }
        : {}
    );
    const getCreditPointsValidationProps = (fieldName) => {
      if (!useHaklaiValidationLayout) return {};
      const error = fieldErrors[fieldName];
      return {
        error: Boolean(error),
        ...getShownErrorProps(error ? (CREDIT_POINTS_COMPACT_ERRORS[error] || "לא תקין") : "", { reserve: true }),
        onBlur: () => this.handleFieldBlur(fieldName)
      };
    };
	    const prisaYearsOptions = this.getPrisaYearsOptions(this.props.maxYears);
    const shouldShowClientsCount = (
      this.props.shouldTryPrisa
      && this.props.soldPropertyDate
      && this.props.maxYears >= 1
      && this.props.minYears !== 0
      && !this.props.isClientsNumDisabled
    );
    const accordionProps = {};
    if (typeof this.props.expanded === "boolean") {
      accordionProps.expanded = this.props.expanded;
      accordionProps.onToggleExpanded = this.props.onToggleExpanded;
      accordionProps.defaultExpanded = false;
    } else {
      accordionProps.defaultExpanded = true;
    }

    const mainGrid = (
      <Grid container direction="column" spacing={2}>
          <Grid container item direction="row" spacing={5} alignItems="flex-start">
            {this.props.shouldTryPrisaShown ? (
              <Fragment>
                <Grid item data-testid="haklai-prisa-choice">
                  <FormControl component="fieldset" disabled={this.props.maxYears < 1 || this.props.minYears === 0}>
                    <Grid container direction="row" spacing={1} alignItems="center">
                      <Grid item>
                        <Typography variant="subtitle1" align="right">
                          {HAKLAI_PRISA_LABELS.shouldTryPrisa}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <HelpDialog
                          helpDialog={helpTexts.shouldTryPrisa}
                          label={HAKLAI_PRISA_LABELS.shouldTryPrisa}
                        />
                      </Grid>
                      <Grid item>
                        <RadioGroup
                          row
                          name="shouldTryPrisa"
                          value={Boolean(this.props.shouldTryPrisa).toString()}
                          onChange={(e) => this.props.handlePrisaOnChange(e)}
                        >
                          <FormControlLabel
                            value={"true"}
                            control={<Radio color="primary" />}
                            label={HAKLAI_PRISA_LABELS.yes}
                            labelPlacement="end"
                          />
                          <FormControlLabel
                            value={"false"}
                            control={<Radio color="primary" />}
                            label={HAKLAI_PRISA_LABELS.no}
                            labelPlacement="end"
                          />
                        </RadioGroup>
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>
              </Fragment>
            ) : null}
            {shouldShowClientsCount ? (
              <Grid item data-testid="haklai-prisa-clients-count">
                <LabeledTextField
                  type="select"
                  value={this.props.personsData.length}
                  textFieldWidth={CLIENTS_COUNT_FIELD_WIDTH}
                  name="personsData"
                  label={HAKLAI_PRISA_LABELS.clientsCount}
                  options={this.props.options}
                  stackLabelOnXs
                  {...getFieldValidationProps("personsData", { reserve: true })}
                  onChange={(e) => {
                    this.props.handlePrisaOnChange(e);
                  }}
                />
              </Grid>
            ) : null}
            {this.props.soldPropertyDateShown ? (
              <Grid item>
                <LabeledTextField
                  type="date"
                  value={this.props.soldPropertyDate}
                  textFieldWidth={PRISA_DATE_FIELD_WIDTH}
                  exactWidth
                  name="soldPropertyDate"
                  label={HAKLAI_PRISA_LABELS.soldPropertyDate}
                  stackLabelOnXs
                  validateInvalidInputOnBlur
                  onChange={(e) => {
                    this.props.handleOnChange(e);
                  }}
                />
              </Grid>
            ) : null}
            {this.props.datePropertyBoughtShown ? (
              <Grid item>
                <LabeledTextField
                  type="date"
                  value={this.props.datePropertyBought}
                  textFieldWidth={PRISA_DATE_FIELD_WIDTH}
                  exactWidth
                  name="datePropertyBought"
                  label={HAKLAI_PRISA_LABELS.datePropertyBought}
                  stackLabelOnXs
                  validateInvalidInputOnBlur
                  onChange={(e) => {
                    this.props.handleOnChange(e);
                  }}
                />
              </Grid>
            ) : null}
            {this.props.maxYears < 1 ? (
              <Grid item>
                <Grid container direction="row" alignItems="center" spacing={1}>
                  <Grid item>
                    <InfoOutlinedIcon color="secondary" style={{ verticalAlign: "middle" }} />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle1" align="right">
                      {`${HAKLAI_PRISA_MESSAGES.sameSalePurchaseYearUnavailable}.`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
	            ) : this.props.minYears === 0 ? (
	              <Grid item>
	                <Grid container direction="row" alignItems="center" spacing={1}>
                  <Grid item>
                    <InfoOutlinedIcon color="secondary" style={{ verticalAlign: "middle" }} />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle1" align="right">
                      {`${HAKLAI_PRISA_MESSAGES.partialPurchaseInSaleYearUnavailable}.`}
                    </Typography>
                  </Grid>
                </Grid>
	              </Grid>
		            ) : null}
		          </Grid>
		          {this.props.shouldTryPrisa && this.props.soldPropertyDate
		            ? this.props.personsData.map((person, index) => {
                return (
                  <Fragment key={`prisa-person-data-${index}`}>
                    <Grid item>
                      <Divider className="haklai-prisa-divider" light />
                    </Grid>
                    {this.props.personsData.length > 1 ? (
                      <Grid item>
                        <Typography align="right" variant="h5" className="haklai-prisa-person-title">
                          {getHaklaiPrisaPersonTitle(index)}
                        </Typography>
                      </Grid>
                    ) : null}
                    <Grid item>
                      <div className="haklai-prisa-person">
                        <div className="haklai-prisa-fields">
                          <LabeledTextField
                            type="text"
                            value={person.personName}
                            textFieldWidth={PRISA_NAME_FIELD_WIDTH}
                            name="personName"
                            label={HAKLAI_PRISA_LABELS.clientName}
                            stackLabelOnXs
                            {...getFieldValidationProps(this.getPersonFieldKey("personName", index))}
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                          <LabeledTextField
                            type="date"
                            value={person.birthOfClient}
                            textFieldWidth={PRISA_DATE_FIELD_WIDTH}
                            exactWidth
                            name="birthOfClient"
                            label={HAKLAI_PRISA_LABELS.birthOfClient}
                            stackLabelOnXs
                            validateInvalidInputOnBlur
                            {...getFieldValidationProps(this.getPersonFieldKey("birthOfClient", index), { reserve: true })}
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                          <LabeledTextField
                            type="select"
                            value={person.dataPerYear.length}
                            textFieldWidth={PRISA_YEARS_FIELD_WIDTH}
                            name="dataPerYear"
                            label={HAKLAI_PRISA_LABELS.yearsCount}
                            options={prisaYearsOptions}
                            stackLabelOnXs
                            {...getFieldValidationProps(this.getPersonFieldKey("dataPerYear", index), { reserve: true })}
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                        </div>
                        <div className="haklai-prisa-fields">
                          <LabeledTextField
                            type="select"
                            value={person.creditPointGender || ""}
                            textFieldWidth={CREDIT_POINT_GENDER_FIELD_WIDTH}
                            name="creditPointGender"
                            label={HAKLAI_PRISA_LABELS.creditPointGender}
                            options={CREDIT_POINT_GENDER_OPTIONS}
                            displayEmptySelectOption
                            stackLabelOnXs
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                          <LabeledTextField
                            type="select"
                            value={person.creditPointResidency || ""}
                            textFieldWidth={CREDIT_POINT_RESIDENCY_FIELD_WIDTH}
                            name="creditPointResidency"
                            label={HAKLAI_PRISA_LABELS.creditPointResidency}
                            options={CREDIT_POINT_RESIDENCY_OPTIONS}
                            displayEmptySelectOption
                            stackLabelOnXs
                            onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                          />
                          <FormControlLabel
                            className="haklai-prisa-maximal"
                            style={CREDIT_POINT_MAXIMAL_STYLE}
                            labelPlacement="end"
                            control={
                              <Checkbox
                                checked={person.isMaximal}
                                value={!person.isMaximal}
                                onChange={(e) => this.props.handlePrisaOnChange(e, index)}
                                name="isMaximal"
                                color="primary"
                              />
                            }
                            label={HAKLAI_PRISA_LABELS.maximalSaleYearCalculation}
                          />
                        </div>
                        {person.dataPerYear.map((year, yearIndex) => {
                          const yearMuted = yearIndex === 0 && person.isMaximal;
                          return (
                            <div
                              className="haklai-prisa-year-row"
                              key={`prisa-person-${index}-data-per-year-${yearIndex}`}
                            >
                              <div className="haklai-prisa-year-cluster">
                                <Typography variant="body1" color={yearMuted ? "textSecondary" : "textPrimary"}>
                                  {HAKLAI_PRISA_LABELS.taxYear}
                                </Typography>
                                <Typography variant="body1" color={yearMuted ? "textSecondary" : "textPrimary"}>
                                  {parseInt(this.props.soldPropertyDate.substring(0, 4)) - yearIndex}
                                </Typography>
                              </div>
                              <div className="haklai-prisa-year-cluster">
                                <Typography variant="body1" color={yearMuted ? "textSecondary" : "textPrimary"}>
                                  {HAKLAI_PRISA_LABELS.income}
                                </Typography>
                                <LabeledTextField
                                  type="number"
                                  textFieldWidth={PRISA_INCOME_FIELD_WIDTH}
                                  value={isDisabledMaximalYear(person, yearIndex)
                                    ? 0
                                    : getPrisaIncomeDisplayValue(year.income)}
                                  name="income"
                                  max={PRISA_INCOME_MAX_VALUE}
                                  disabled={isDisabledMaximalYear(person, yearIndex)}
                                  inputBackgroundOnly={isDisabledMaximalYear(person, yearIndex)}
                                  onChange={(e) => this.props.handlePrisaOnChange(e, index, yearIndex)}
                                />
                              </div>
                              <div className="haklai-prisa-year-cluster">
                                <Typography variant="body1" color={yearMuted ? "textSecondary" : "textPrimary"}>
                                  {HAKLAI_PRISA_LABELS.creditPoints}
                                </Typography>
                                <LabeledTextField
                                  type="number"
                                  textFieldWidth={PRISA_ZIKUI_FIELD_WIDTH}
                                  value={isDisabledMaximalYear(person, yearIndex) ? 0 : year.zikui}
                                  name="zikui"
                                  max={PRISA_ZIKUI_MAX_VALUE}
                                  disabled={isDisabledMaximalYear(person, yearIndex)}
                                  inputBackgroundOnly={isDisabledMaximalYear(person, yearIndex)}
                                  {...getCreditPointsValidationProps(this.getYearFieldKey("zikui", index, yearIndex))}
                                  onChange={(e) => this.props.handlePrisaOnChange(e, index, yearIndex)}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Grid>
                  </Fragment>
                );
              })
            : null}
      </Grid>
    );

    const wrappedMainContent = (
      <Box
        className={[
          "haklai-prisa",
          this.props.shouldTryPrisa ? "" : "is-prisa-off",
          this.props.contentWrapperClassName
        ].filter(Boolean).join(" ")}
        ref={this.props.contentWrapperRef}
        inert={this.props.contentInert ? "" : undefined}
      >
        {mainGrid}
      </Box>
    );

    const AccordionShell = this.props.AccordionComponent || CustomAcordion;
    return (
      <AccordionShell summaryText={HAKLAI_PRISA_LABELS.sectionTitle} {...accordionProps}>
        {this.props.beforeContent || null}
        {wrappedMainContent}
      </AccordionShell>
    );
  }

  getPrisaYearsOptions(maxYears) {
    const options = [];
    for (let i = 1; i <= maxYears; i++) {
      options.push({ label: getHaklaiPrisaYearsBackLabel(i), value: i });
    }
    return options;
  }

  getPersonFieldKey(fieldName, personIndex) {
    return `${fieldName}_${personIndex}`;
  }

  getYearFieldKey(fieldName, personIndex, yearIndex) {
    return `${fieldName}_${personIndex}_${yearIndex}`;
  }

  handleFieldBlur(fieldName) {
    if (!fieldName || typeof this.props.onFieldBlur !== "function") return;
    this.props.onFieldBlur(fieldName);
  }
}

export default HaklaiPrisaComponent;
